
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertTriangle, Home } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PropertyAmenitiesDisplay from '@/components/property/PropertyAmenitiesDisplay';
import ListingDetailInfo from '@/components/ListingDetailInfo';
import ListingDetailActions from '@/components/ListingDetailActions';

interface Listing {
  id: string;
  title: string;
  description: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  gender_rules: string;
  lease_min_months: number;
  available_from: string;
  photos: string[];
  approval_status: string;
  status: string;
  rental_status: string;
  verified: boolean;
  distance_km: number;
  owner_id: string;
  amenities: string[] | null;
  universities: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
  profiles: {
    full_name: string;
    phone: string;
    email: string;
  } | null;
}

const ListingDetail = () => {
  const { id } = useParams();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    if (id) {
      fetchListingDetails();
    }
  }, [id]);

  const fetchListingDetails = async () => {
    if (!id) return;

    try {
      setLoading(true);
      
      const { data: listingData, error: listingError } = await supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          )
        `)
        .eq('id', id)
        .single();

      if (listingError) {
        console.error('Error fetching listing:', listingError);
        toast.error('Failed to load listing details');
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, phone, email')
        .eq('id', listingData.owner_id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      const combinedData = {
        ...listingData,
        profiles: profileData || null
      };

      setListing(combinedData as Listing);
    } catch (error) {
      console.error('Exception fetching listing details:', error);
      toast.error('Failed to load listing details');
    } finally {
      setLoading(false);
    }
  };

  const handleWishlistToggle = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error(language === 'ar' ? 'يجب تسجيل الدخول أولاً' : 'Please sign in first');
        return;
      }

      setIsWishlisted(!isWishlisted);
      toast.success(
        isWishlisted 
          ? (language === 'ar' ? 'تم إزالة العقار من المفضلة' : 'Removed from wishlist')
          : (language === 'ar' ? 'تم إضافة العقار للمفضلة' : 'Added to wishlist')
      );
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error(language === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const handleScheduleVisit = () => {
    toast.info(t('visit_scheduling_coming_soon', 'ميزة جدولة الزيارة قريباً', 'Visit scheduling feature coming soon'));
  };

  const handleOpenChat = () => {
    toast.info(t('chat_feature_coming_soon', 'ميزة المحادثة قريباً', 'Chat feature coming soon'));
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className={`min-h-screen ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">
              {t('listing_not_found', 'العقار غير موجود', 'Listing not found')}
            </h1>
            <p className="text-muted-foreground">
              {t('listing_not_found_desc', 'هذا العقار غير موجود أو غير متاح للعرض', 'This listing does not exist or is not available for viewing')}
            </p>
            <Button onClick={handleBackClick} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'ar' ? 'العودة' : 'Go Back'}
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <Header />
      <div className="container mx-auto px-4 py-4 md:py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={handleBackClick}
          className="mb-4 md:mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === 'ar' ? 'العودة' : 'Back'}
        </Button>

        {/* Show approval status banner if not approved */}
        {listing.approval_status !== 'approved' && (
          <Alert className="mb-4 md:mb-6 border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              {listing.approval_status === 'pending' && 
                t('listing_pending', 'هذا العقار في انتظار الموافقة', 'This listing is pending approval')
              }
              {listing.approval_status === 'rejected' && 
                t('listing_rejected', 'هذا العقار مرفوض', 'This listing has been rejected')
              }
            </AlertDescription>
          </Alert>
        )}

        {/* Show rented status banner */}
        {listing.rental_status === 'rented' && (
          <Alert className="mb-4 md:mb-6 border-blue-200 bg-blue-50">
            <Home className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              {t('property_rented', 'هذا العقار مؤجر حالياً وغير متاح للإيجار', 'This property is currently rented and not available for lease')}
            </AlertDescription>
          </Alert>
        )}

        {/* Mobile First Layout */}
        <div className="space-y-6">
          {/* Images Section */}
          <div className="w-full">
            {listing.photos && listing.photos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {listing.photos.map((photo, index) => (
                  <div key={index} className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img 
                      src={photo} 
                      alt={`${listing.title} - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                <Home className="h-12 w-12 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Property Info Section */}
          <ListingDetailInfo listing={listing} />

          {/* Action Buttons - Prominently displayed on mobile */}
          <div className="md:hidden">
            <ListingDetailActions 
              listing={listing}
              isInWishlist={isWishlisted}
              onToggleWishlist={handleWishlistToggle}
              onScheduleVisit={handleScheduleVisit}
              onOpenChat={handleOpenChat}
            />
          </div>

          {/* Desktop/Tablet Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardContent className="p-4 md:p-6">
                  <h3 className="text-lg font-semibold mb-3">
                    {t('description', 'الوصف', 'Description')}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {listing.description}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 md:p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {t('property_details', 'تفاصيل العقار', 'Property Details')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">{t('address', 'العنوان', 'Address')}</span>
                      <span className="font-medium text-right">{listing.address}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">{t('available_from', 'متاح من', 'Available from')}</span>
                      <span className="font-medium">{new Date(listing.available_from).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="text-muted-foreground">{t('furnished', 'مفروش', 'Furnished')}</span>
                      <span className="font-medium">{listing.furnished ? t('yes', 'نعم', 'Yes') : t('no', 'لا', 'No')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <PropertyAmenitiesDisplay amenities={listing.amenities || []} />
            </div>
            
            {/* Desktop Contact Card */}
            <div className="hidden md:block lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle>{t('contact_owner', 'تواصل مع المالك', 'Contact Owner')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold">{listing.profiles?.full_name || 'Property Owner'}</h4>
                    <p className="text-sm text-muted-foreground">
                      95% {t('response_rate', 'معدل الاستجابة', 'response rate')}
                    </p>
                  </div>
                  <ListingDetailActions 
                    listing={listing}
                    isInWishlist={isWishlisted}
                    onToggleWishlist={handleWishlistToggle}
                    onScheduleVisit={handleScheduleVisit}
                    onOpenChat={handleOpenChat}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Mobile Sticky Bottom Actions */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t p-4 z-50">
          <div className="container mx-auto max-w-sm">
            <ListingDetailActions 
              listing={listing}
              isInWishlist={isWishlisted}
              onToggleWishlist={handleWishlistToggle}
              onScheduleVisit={handleScheduleVisit}
              onOpenChat={handleOpenChat}
            />
          </div>
        </div>

        {/* Add bottom padding for mobile to account for sticky buttons */}
        <div className="md:hidden h-32"></div>
      </div>
      <Footer />
    </div>
  );
};

export default ListingDetail;
